import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Select, message } from 'antd';
import { useAuth0 } from '@auth0/auth0-react';
import { theme } from '../../../theme';

const { Option } = Select;

interface EquipoModalProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
  equipo?: {
    id: number;
    descripcion: string;
    id_sector: number;
    alta?: number;
  } | null;
}

interface SectorData {
  id: number;
  descripcion: string;
  alta: number;
}

const EquipoModal: React.FC<EquipoModalProps> = ({ visible, onClose, onSuccess, equipo }) => {
  const { user, getAccessTokenSilently } = useAuth0();
  const empresa = user?.id_empresa;
  const sucursal = user?.id_sucursal;
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState(false);
  const [sectores, setSectores] = useState<SectorData[]>([]);
  const [loadingSectores, setLoadingSectores] = useState(false);

  const isEditing = !!equipo;

  // Cargar sectores
  const fetchSectores = async () => {
    setLoadingSectores(true);
    try {
      const accessToken = await getAccessTokenSilently();
      const response = await fetch(`${process.env.REACT_APP_INSUMOS_URL}/listar_sectores/${empresa}/${sucursal}`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) throw new Error('Error al cargar sectores');

      const data = await response.json();
      const activos = data.filter((item: SectorData) => item.alta === 1);
      setSectores(activos);
    } catch (error) {
      message.error('Error al cargar los sectores');
    } finally {
      setLoadingSectores(false);
    }
  };

  // Cargar datos si es edición
  useEffect(() => {
    if (visible) {
      fetchSectores();
      if (equipo) {
        form.setFieldsValue({
          descripcion: equipo.descripcion,
          sector: equipo.id_sector,
          alta: equipo.alta !== undefined ? equipo.alta : 1
        });
      } else {
        form.resetFields();
      }
    }
  }, [visible, equipo, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const accessToken = await getAccessTokenSilently();
      const url = isEditing
        ? `${process.env.REACT_APP_INSUMOS_URL}/editar_equipos/${empresa}/${sucursal}/${equipo!.id}`
        : `${process.env.REACT_APP_INSUMOS_URL}/crear_equipos`;

      const response = await fetch(url, {
        method: 'POST', // Siempre POST (tanto para crear como editar)
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          sector: values.sector,
          descripcion: values.descripcion,
          alta: values.alta,
          ...(isEditing ? {} : {
            id_empresa: empresa,
            id_sucursal: sucursal,
          })
        }),
      });

      if (!response.ok) {
        throw new Error('Error en la respuesta del servidor');
      }

      message.success(`Equipo ${isEditing ? 'actualizado' : 'creado'} correctamente`);
      form.resetFields();
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error:', error);
      message.error(`Error al ${isEditing ? 'actualizar' : 'crear'} el equipo`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <span style={{ 
          color: theme.colors.primaryDark, 
          fontSize: theme.fontSizes.large,
          fontWeight: 600
        }}>
          {isEditing ? 'Editar Equipo' : 'Nuevo Equipo'}
        </span>
      }
      open={visible}
      onCancel={onClose}
      onOk={handleSubmit}
      confirmLoading={loading}
      okText={isEditing ? 'Actualizar' : 'Crear'}
      cancelText="Cancelar"
      okButtonProps={{
        style: {
          backgroundColor: theme.colors.secondary,
          borderColor: theme.colors.secondary,
          fontWeight: 500
        }
      }}
      cancelButtonProps={{
        style: {
          fontWeight: 500
        }
      }}
    >
      <Form
        form={form}
        layout="vertical"
        style={{ marginTop: '20px' }}
        initialValues={{
          alta: 1 // Activo por defecto para nuevos equipos
        }}
      >
        <Form.Item 
          name="descripcion" 
          label={
            <span style={{ 
              color: theme.colors.textPrimary, 
              fontWeight: 600 
            }}>
              Descripción
            </span>
          }
          rules={[{ required: true, message: 'Por favor ingrese una descripción' }]}
        >
          <Input 
            maxLength={30} 
            placeholder="Ingrese la descripción del equipo"
          />
        </Form.Item>

        <Form.Item 
          name="sector" 
          label={
            <span style={{ 
              color: theme.colors.textPrimary, 
              fontWeight: 600 
            }}>
              Sector
            </span>
          }
          rules={[{ required: true, message: 'Por favor seleccione un sector' }]}
        >
          <Select
            placeholder="Seleccione un sector"
            loading={loadingSectores}
          >
            {sectores.map((sector) => (
              <Option key={sector.id} value={sector.id}>
                {sector.descripcion}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item 
          name="alta" 
          label={
            <span style={{ 
              color: theme.colors.textPrimary, 
              fontWeight: 600 
            }}>
              Estado
            </span>
          }
          rules={[{ required: true, message: 'Por favor seleccione el estado' }]}
        >
          <Select placeholder="Seleccione el estado">
            <Option value={1}>Activo</Option>
            <Option value={0}>Inactivo</Option>
          </Select>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default EquipoModal;

